import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { 
  Plus, 
  Server, 
  Wifi, 
  WifiOff, 
  Settings, 
  Trash2,
  Folder,
  Monitor,
  Camera,
  X,
  Info,
  Pencil
} from 'lucide-react';
import { deviceService } from '../services/deviceService';
import { cctvService } from '../services/cctvService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Devices = () => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: devicesData, isLoading } = useQuery(
    ['devices', page],
    () => deviceService.getDevices(page, 10)
  );

  const { data: camerasData, isLoading: camerasLoading } = useQuery(
    ['cameras', selectedDevice?._id],
    () => cctvService.getCameras(selectedDevice._id),
    {
      enabled: !!selectedDevice && selectedDevice.type === 'camera_recorder',
      onError: (error) => {
        console.error('Błąd pobierania kamer:', error);
      }
    }
  );

  const addDeviceMutation = useMutation(deviceService.addDevice, {
    onSuccess: () => {
      queryClient.invalidateQueries('devices');
      closeFormModal();
      toast.success('Urządzenie zostało dodane');
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map(err => err.msg || err.message).join(', ');
        toast.error(`Błędy walidacji: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.message || 'Błąd podczas dodawania urządzenia');
      }
    }
  });

  const updateDeviceMutation = useMutation(
    ({ id, data }) => deviceService.updateDevice(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('devices');
        closeFormModal();
        toast.success('Urządzenie zostało zaktualizowane');
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          const validationErrors = error.response.data.errors;
          const errorMessages = validationErrors.map(err => err.msg || err.message).join(', ');
          toast.error(`Błędy walidacji: ${errorMessages}`);
        } else {
          toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji urządzenia');
        }
      }
    }
  );

  const deleteDeviceMutation = useMutation(deviceService.deleteDevice, {
    onSuccess: () => {
      queryClient.invalidateQueries('devices');
      toast.success('Urządzenie zostało usunięte');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania urządzenia');
    }
  });

  const checkStatusMutation = useMutation(deviceService.checkDeviceStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries('devices');
      toast.success('Status urządzenia został sprawdzony');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas sprawdzania statusu');
    }
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const closeFormModal = () => {
    reset({
      name: '',
      type: '',
      ipAddress: '',
      port: 80,
      credentials: { username: '', password: '' }
    });
    setEditingDevice(null);
    setShowFormModal(false);
  };

  const openAddModal = () => {
    setEditingDevice(null);
    reset({
      name: '',
      type: '',
      ipAddress: '',
      port: 80,
      credentials: { username: '', password: '' }
    });
    setShowFormModal(true);
  };

  const openEditModal = (device) => {
    setEditingDevice(device);
    reset({
      name: device.name || '',
      type: device.type || '',
      ipAddress: device.ipAddress || '',
      port: device.port || 80,
      credentials: {
        username: device.credentials?.username || '',
        password: ''
      }
    });
    setShowFormModal(true);
  };

  const onSubmit = (data) => {
    const port = data.port ? parseInt(data.port, 10) : 80;

    const deviceData = {
      name: data.name?.trim(),
      type: data.type,
      ipAddress: data.ipAddress?.trim(),
      port: isNaN(port) ? 80 : port,
      credentials: {
        username: data.credentials?.username?.trim() || '',
        password: data.credentials?.password || ''
      }
    };

    if (!deviceData.name || !deviceData.type) {
      toast.error('Nazwa i typ urządzenia są wymagane');
      return;
    }

    const validTypes = ['nas', 'router', 'switch', 'server', 'camera_recorder', 'other'];
    if (!validTypes.includes(deviceData.type)) {
      toast.error(`Nieprawidłowy typ urządzenia. Dozwolone typy: ${validTypes.join(', ')}`);
      return;
    }

    if (!deviceData.credentials.username) {
      toast.error('Nazwa użytkownika jest wymagana');
      return;
    }

    // Przy dodawaniu hasło jest wymagane; przy edycji puste = bez zmiany
    if (!editingDevice && !deviceData.credentials.password) {
      toast.error('Hasło jest wymagane');
      return;
    }

    if (deviceData.port < 1 || deviceData.port > 65535) {
      toast.error('Port musi być między 1 a 65535');
      return;
    }

    if (editingDevice) {
      updateDeviceMutation.mutate({ id: editingDevice._id, data: deviceData });
    } else {
      addDeviceMutation.mutate(deviceData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Czy na pewno chcesz usunąć to urządzenie?')) {
      deleteDeviceMutation.mutate(id);
    }
  };

  const handleCheckStatus = (id) => {
    checkStatusMutation.mutate(id);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      online: 'status-online',
      offline: 'status-offline',
      unknown: 'bg-gray-100 text-gray-800'
    };

    const statusText = {
      online: 'Online',
      offline: 'Offline',
      unknown: 'Nieznany'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status]}`}>
        {status === 'online' ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
        {statusText[status]}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'nas':
        return <Server className="h-5 w-5 text-blue-600" />;
      case 'router':
        return <Wifi className="h-5 w-5 text-green-600" />;
      case 'switch':
        return <Settings className="h-5 w-5 text-purple-600" />;
      case 'server':
        return <Monitor className="h-5 w-5 text-orange-600" />;
      case 'camera_recorder':
        return <Camera className="h-5 w-5 text-red-600" />;
      default:
        return <Server className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTypeLabel = (type) => {
    const typeLabels = {
      'nas': 'NAS',
      'router': 'Router',
      'switch': 'Switch',
      'server': 'Server',
      'camera_recorder': 'Rejestrator kamer',
      'other': 'Inne'
    };
    return typeLabels[type] || type;
  };

  const isSaving = addDeviceMutation.isLoading || updateDeviceMutation.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={openAddModal}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Dodaj urządzenie
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {devicesData?.devices?.map((device) => (
          <div key={device._id} className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {getTypeIcon(device.type)}
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {device.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {device.ipAddress}:{device.port}
                    </p>
                  </div>
                </div>
                {getStatusBadge(device.status)}
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Typ: {getTypeLabel(device.type)}</span>
                  <span>
                    Ostatnio widziany: {new Date(device.lastSeen).toLocaleDateString('pl-PL')}
                  </span>
                </div>
              </div>

              {device.folders && device.folders.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <Folder className="h-4 w-4 mr-1" />
                    {device.folders.length} folderów
                  </div>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => handleCheckStatus(device._id)}
                  disabled={checkStatusMutation.isLoading}
                  className="flex-1 btn-secondary text-xs"
                >
                  {checkStatusMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Sprawdź status'
                  )}
                </button>
                <button
                  onClick={() => openEditModal(device)}
                  className="flex-1 btn-secondary text-xs"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1 inline" />
                  Edytuj
                </button>
                <button
                  onClick={() => setSelectedDevice(device)}
                  className="flex-1 btn-secondary text-xs"
                >
                  Szczegóły
                </button>
                <button
                  onClick={() => handleDelete(device._id)}
                  disabled={deleteDeviceMutation.isLoading}
                  className="btn-danger text-xs px-3"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {devicesData?.pagination && devicesData.pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Strona {devicesData.pagination.current} z {devicesData.pagination.pages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={!devicesData.pagination.hasPrev}
              className="btn-secondary"
            >
              Poprzednia
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={!devicesData.pagination.hasNext}
              className="btn-secondary"
            >
              Następna
            </button>
          </div>
        </div>
      )}

      {selectedDevice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Info className="h-5 w-5 mr-2 text-blue-600" />
                  Szczegóły urządzenia
                </h3>
                <button
                  onClick={() => setSelectedDevice(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Informacje podstawowe</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Nazwa:</span>
                      <span className="ml-2 font-medium text-gray-900">{selectedDevice.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Typ:</span>
                      <span className="ml-2 font-medium text-gray-900">{getTypeLabel(selectedDevice.type)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Adres IP:</span>
                      <span className="ml-2 font-medium text-gray-900">{selectedDevice.ipAddress}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Port:</span>
                      <span className="ml-2 font-medium text-gray-900">{selectedDevice.port}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <span className="ml-2">{getStatusBadge(selectedDevice.status)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Ostatnio widziany:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {new Date(selectedDevice.lastSeen).toLocaleString('pl-PL')}
                      </span>
                    </div>
                    {selectedDevice.credentials?.username && (
                      <div>
                        <span className="text-gray-500">Użytkownik:</span>
                        <span className="ml-2 font-medium text-gray-900">{selectedDevice.credentials.username}</span>
                      </div>
                    )}
                  </div>
                </div>

                {selectedDevice.type === 'camera_recorder' && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Camera className="h-5 w-5 mr-2 text-blue-600" />
                      Kamery
                    </h4>
                    {camerasLoading ? (
                      <div className="flex items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2 text-sm text-gray-600">Pobieranie liczby kamer...</span>
                      </div>
                    ) : camerasData?.cameras ? (
                      <div className="text-lg">
                        <span className="text-gray-700">Liczba kamer na urządzeniu: </span>
                        <span className="font-bold text-blue-600">{camerasData.cameras.length}</span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        Nie udało się pobrać liczby kamer. Sprawdź połączenie z urządzeniem.
                      </div>
                    )}
                  </div>
                )}

                {selectedDevice.folders && selectedDevice.folders.length > 0 && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Folder className="h-5 w-5 mr-2 text-green-600" />
                      Foldery
                    </h4>
                    <div className="text-lg">
                      <span className="text-gray-700">Liczba folderów: </span>
                      <span className="font-bold text-green-600">{selectedDevice.folders.length}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedDevice(null);
                    openEditModal(selectedDevice);
                  }}
                  className="btn-primary"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edytuj
                </button>
                <button
                  onClick={() => setSelectedDevice(null)}
                  className="btn-secondary"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingDevice ? 'Edytuj urządzenie' : 'Dodaj nowe urządzenie'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nazwa urządzenia
                  </label>
                  <input
                    {...register('name', { required: 'Nazwa jest wymagana' })}
                    type="text"
                    className="input"
                    placeholder="np. NAS Server 1"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Typ urządzenia
                  </label>
                  <select
                    {...register('type', { required: 'Typ jest wymagany' })}
                    className="input"
                  >
                    <option value="">Wybierz typ</option>
                    <option value="nas">NAS</option>
                    <option value="router">Router</option>
                    <option value="switch">Switch</option>
                    <option value="server">Server</option>
                    <option value="camera_recorder">Rejestrator kamer</option>
                    <option value="other">Inne</option>
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Adres IP
                  </label>
                  <input
                    {...register('ipAddress', { 
                      required: 'Adres IP jest wymagany',
                      pattern: {
                        value: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                        message: 'Nieprawidłowy adres IP'
                      }
                    })}
                    type="text"
                    className="input"
                    placeholder="192.168.1.100"
                  />
                  {errors.ipAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.ipAddress.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Port
                  </label>
                  <input
                    {...register('port', { 
                      valueAsNumber: true,
                      min: { value: 1, message: 'Port musi być większy niż 0' },
                      max: { value: 65535, message: 'Port nie może przekraczać 65535' },
                      validate: (value) => {
                        if (value === '' || value === null || value === undefined) {
                          return true;
                        }
                        const numValue = Number(value);
                        if (isNaN(numValue)) {
                          return 'Port musi być liczbą';
                        }
                        if (numValue < 1 || numValue > 65535) {
                          return 'Port musi być między 1 a 65535';
                        }
                        return true;
                      }
                    })}
                    type="number"
                    className="input"
                    placeholder="80"
                  />
                  {errors.port && (
                    <p className="mt-1 text-sm text-red-600">{errors.port.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nazwa użytkownika
                  </label>
                  <input
                    {...register('credentials.username', { required: 'Nazwa użytkownika jest wymagana' })}
                    type="text"
                    className="input"
                    placeholder="admin"
                  />
                  {errors.credentials?.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.credentials.username.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Hasło
                  </label>
                  <input
                    {...register('credentials.password', {
                      required: editingDevice ? false : 'Hasło jest wymagane'
                    })}
                    type="password"
                    className="input"
                    placeholder={editingDevice ? 'Zostaw puste, aby nie zmieniać' : '••••••••'}
                  />
                  {errors.credentials?.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.credentials.password.message}</p>
                  )}
                  {editingDevice && (
                    <p className="mt-1 text-xs text-gray-500">
                      Puste hasło = bez zmiany istniejącego
                    </p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeFormModal}
                    className="flex-1 btn-secondary"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex-1 btn-primary"
                  >
                    {isSaving ? (
                      <LoadingSpinner size="sm" />
                    ) : editingDevice ? (
                      'Zapisz zmiany'
                    ) : (
                      'Dodaj urządzenie'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;
